/**
 * Pedaços da planilha que Transações e Contas compartilham: formatação e parse de célula e o
 * menu flutuante de uma célula (busca, Enter escolhe a primeira, Esc fecha, Tab avança).
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { normalize } from '../lib/text';
import { CategoryIcon } from './CategoryIcon';

export interface Opt { id: string; label: string; icon?: ReactNode; muted?: boolean }

export const TOAST_MS = 8000;
export const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
export const fmtAmount = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "12/10", "12/10/26", "12/10/2026" ou "2026-10-12" → ISO; null se não der. O ano vem da linha quando falta. */
export function parseDay(raw: string, refIso: string): string | null {
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?$/.exec(s);
  if (!m) return null;
  const d = Number(m[1]); const mo = Number(m[2]);
  const y = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : Number(refIso.slice(0, 4));
  if (d < 1 || d > 31 || mo < 1 || mo > 12) return null;
  const dt = new Date(y, mo - 1, d);
  if (dt.getMonth() !== mo - 1) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Só valores positivos; sinal digitado é ignorado (o lado é o tipo da linha). */
export function parseAmount(raw: string): number | null {
  const s = raw.trim().replace(/\s|R\$/g, '').replace(/^[+−-]/, '');
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Valor com sinal, como o usuário digita num ajuste: "-120,00", "1.200" → número; null se não der. */
export function parseSigned(raw: string): number | null {
  const s = raw.trim().replace(/\s|R\$/g, '');
  const neg = /^[−-]/.test(s);
  const body = s.replace(/^[+−-]/, '');
  const n = Number(body.includes(',') ? body.replace(/\./g, '').replace(',', '.') : body);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

export const catChip = (color: string, name: string): ReactNode => (
  <span className="is-cat" style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)` }}><CategoryIcon name={name} size={12} /></span>
);

/** Lista flutuante de uma célula ou do lote: busca, Enter escolhe a primeira, Esc fecha. */
export function CellMenu({ options, selected, hint, onPick, onClose, onTab, align = 'left' }: {
  options: Opt[]; selected: string | null; hint: string; onPick: (id: string) => void; onClose: () => void; onTab?: (dir: 1 | -1) => void; align?: 'left' | 'right';
}) {
  const [q, setQ] = useState('');
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (root.current && !root.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);
  const nq = normalize(q.trim());
  const shown = nq ? options.filter(o => normalize(o.label).includes(nq)) : options;
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'Enter') { e.preventDefault(); if (shown[0]) onPick(shown[0].id); }
    else if (e.key === 'Tab' && onTab) { e.preventDefault(); onTab(e.shiftKey ? -1 : 1); }
  };
  return (
    // O menu mora dentro da célula: sem parar o clique aqui, escolher uma opção sobe até o onClick
    // da célula, que abre o menu de novo logo depois do fechamento.
    <div ref={root} className={`sheet-menu ${align === 'right' ? 'is-right' : ''}`} role="listbox" onClick={e => e.stopPropagation()}>
      <label className="sheet-menu-search">
        <CategoryIcon name="magnifying-glass" size={13} />
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} placeholder={hint} aria-label={hint} />
      </label>
      <div className="sheet-menu-list">
        {shown.map(o => (
          <button key={o.id} type="button" role="option" aria-selected={o.id === selected} className={`${o.id === selected ? 'is-on' : ''} ${o.muted ? 'is-muted' : ''}`} onClick={() => onPick(o.id)}>
            {o.icon}<span>{o.label}</span>{o.id === selected && <CategoryIcon name="check" size={13} className="text-teal" />}
          </button>
        ))}
        {shown.length === 0 && <span className="sheet-menu-empty">Nada encontrado</span>}
      </div>
    </div>
  );
}
